import {
    LEGAL_SOURCE_TRUE_ID,
    NO_TEXT,
    NOT_APPLICABLE_TEXT,
    QUOTA_TYPE_CANDIDATE_QUOTA_ID,
    QUOTA_TYPE_RESERVED_SEATS_ID,
    RANKING_PLACEMENT_NA_ID,
    RANKING_PLACEMENT_NO_ID,
    SANCTIONS_NA_ID,
    SANCTIONS_NO_ID,
    YES_TEXT
} from "../../../constants";
import { useAppState } from "../../../hooks/use-app-state.hook";
import { CountryProfile } from '../../../types'
import { ThemeName } from "../../../types"
import { getTaxonomyName, getTaxonomyNameList, textPrefix } from "../../../utils";
import Box from "../../layout/box/box.component";
import Comments from "../../layout/comments/comments.component";
import TargetAndRepresentationDonuts from "../target-and-representation-donuts/target-and-representation-donuts.component";
import { Cell, Grid } from "../../layout/grid/grid.component";
import Spacer from "../../layout/spacer/spacer.component";
import {
    countryProfileDashboardContent1ClassName,
    countryProfileDashboardContent2ClassName,
    countryProfileDashboardContentBoxClassName,
    countryProfileDashboardDonutsWrapperClassName,
    countryProfileDashboardImplementationDescriptionClassName,
    countryProfileDashboardImplementationTitleClassName,
    countryProfileDashboardLegalProvisionsWrapperClassName,
    countryProfileDashboardQuotaTypeNameClassName,
    countryProfileDashboardSectionContentClassName,
    countryProfileDashboardSectionTitleClassName,
    countryProfileDashboardTitleClassName
} from "./country-profile-dashboard.style";
import LegalProvisions from "../legal-provisions/legal-provisions.component";
import InfoDate from "../info-date/info-date.component";
import CheckList from "../../layout/checklist/checklist.component";

interface CountryProfileDashboardProps {
    title: string,
    countryProfile: CountryProfile
    theme?: ThemeName
}

const CountryProfileSection = ({
    title, children
}: {title?: string, children?: React.ReactNode}) => (
    <>
        { title && (
            <div className={ countryProfileDashboardSectionTitleClassName }>
                <Spacer>
                    { title }
                </Spacer>
            </div>
        ) }
        <div className={ countryProfileDashboardSectionContentClassName }>
            { children }
        </div>
    </>
);

const QuotaImplementationItem = ({
    title, children
}: {title: string, children?: React.ReactNode}) => (
    <>
        <div className={ countryProfileDashboardImplementationTitleClassName }>
            { title }
        </div>
        <div className={ countryProfileDashboardImplementationDescriptionClassName }>
            { children }
        </div>
    </>
);

// const is = {
//     na: (value: string) => value === NA,
// };

export const CountryProfileDashboard: React.FC<CountryProfileDashboardProps> = ({
    title,
    countryProfile,
    theme = 'blue'
}) => {
    const [{
        taxonomies
    }] = useAppState();

    const showTypeOfQuotaTitles = (
        (countryProfile.typeOfQuotaIds.indexOf(QUOTA_TYPE_RESERVED_SEATS_ID) !== -1)
        && (countryProfile.typeOfQuotaIds.indexOf(QUOTA_TYPE_CANDIDATE_QUOTA_ID) !== -1)
    );
    
    const countryProfileTaxonomyNames = {
        country: getTaxonomyName(taxonomies, 'countries', countryProfile.countryId),
        region: getTaxonomyName(taxonomies, 'regions', countryProfile.regionId),
        parliamentStructure: getTaxonomyName(taxonomies, 'parliamentStructure', countryProfile.parliamentStructureId),
        electoralSystemType: getTaxonomyName(taxonomies, 'electoralSystemTypes', countryProfile.electoralSystemTypeId),
        quotaTargetMet: getTaxonomyName(taxonomies, 'quotaTargetMet', countryProfile.quotaTargetMetId),
        quotaForInputOnly: getTaxonomyName(taxonomies, 'quotaForInputOnly', countryProfile.quotaForInputOnlyId),
        typeOfQuotas: countryProfile.typeOfQuotaIds.map(typeOfQuotaId => getTaxonomyName(taxonomies, 'typesOfQuota', typeOfQuotaId)).join(', ')
    };

    return (
        <div>
            
            <div className={ countryProfileDashboardContent1ClassName }>
                <Spacer/>
                <Grid>
                    <Cell size={ 12 }>
                        <div className={ countryProfileDashboardTitleClassName }>
                            <Spacer/>
                            { title }
                        </div>
                    </Cell>
                    <Cell size={ 12 } sizeDesktop={ 7 }>
                        <Box className={ countryProfileDashboardContentBoxClassName }>
                            <CountryProfileSection title="Quota type and electoral system">
                                <Grid>
                                    <Cell size={ 12 } sizeTablet={ 'fill' } className={ countryProfileDashboardDonutsWrapperClassName }>
                                        <TargetAndRepresentationDonuts
                                            countryProfile={ countryProfile }
                                            theme={ theme }/>
                                    </Cell>
                                    <Cell size={ 12 } sizeTablet={ 4 }>
                                        <QuotaImplementationItem title="Electoral system">
                                            { countryProfileTaxonomyNames.electoralSystemType }
                                        </QuotaImplementationItem>
                                        <QuotaImplementationItem title="Electoral system sub-type">
                                            { countryProfile.electoralSystemSubType }
                                        </QuotaImplementationItem>
                                        <QuotaImplementationItem title="Type of quota">
                                            { getTaxonomyNameList(taxonomies, 'typesOfQuota', countryProfile.typeOfQuotaIds).map(
                                                (taxonomyName, index) => (
                                                    <div key={ index }>
                                                        { taxonomyName }
                                                    </div>
                                                )
                                            ) }
                                        </QuotaImplementationItem>
                                    </Cell>
                                </Grid>
                                <Comments name="Note">
                                    { countryProfile.comments }
                                </Comments>
                            </CountryProfileSection>
                        </Box>
                    </Cell>
                    <Cell size={ 12 } sizeDesktop={ 5 }>
                        <Box className={ countryProfileDashboardContentBoxClassName }>
                            <CountryProfileSection title="Quota implementation">
                                { countryProfile.typeOfQuotaIds.indexOf(QUOTA_TYPE_RESERVED_SEATS_ID) > -1 && (
                                    <>
                                        { showTypeOfQuotaTitles && (
                                            <div className={ countryProfileDashboardQuotaTypeNameClassName }>
                                                Reserved seats
                                            </div>
                                        ) }
                                        <QuotaImplementationItem title="Method of filling seats">
                                            { countryProfile.methodOfFillingSeats }
                                        </QuotaImplementationItem>
                                    </>
                                ) }
                                { countryProfile.typeOfQuotaIds.indexOf(QUOTA_TYPE_CANDIDATE_QUOTA_ID) > -1 && (
                                    <>
                                        { showTypeOfQuotaTitles && (
                                            <div className={ countryProfileDashboardQuotaTypeNameClassName }>
                                                Candidate quota
                                            </div>
                                        ) }
                                        { countryProfile.rankingPlacementId !== RANKING_PLACEMENT_NA_ID && (
                                            <QuotaImplementationItem title="Ranking/placement rules">
                                                {
                                                    (countryProfile.rankingPlacementId !== RANKING_PLACEMENT_NO_ID)
                                                    &&
                                                    (countryProfile.rankingPlacementId !== 0)
                                                    ? (
                                                        <>
                                                            { YES_TEXT }
                                                            { textPrefix('. ', countryProfile.rankingPlacementDetails) }
                                                        </>
                                                    )
                                                    : NO_TEXT
                                                }
                                            </QuotaImplementationItem>
                                        ) }
                                        <QuotaImplementationItem title="Sanctions for non-compliance">
                                            {
                                                countryProfile.sanctionId === SANCTIONS_NA_ID
                                                ? NOT_APPLICABLE_TEXT
                                                : (
                                                    countryProfile.sanctionId === SANCTIONS_NO_ID
                                                    || (countryProfile.sanctionId === 0)
                                                    ? NO_TEXT
                                                    : (
                                                        <>
                                                            { YES_TEXT }
                                                            { textPrefix('. ', countryProfile.listsAreRejected) }
                                                            { textPrefix('. ', countryProfile.otherSanctions) }
                                                        </>
                                                    )
                                                )
                                            }
                                        </QuotaImplementationItem>
                                        { (
                                            (countryProfile.quotaForInputOnlyId !== 0)
                                            &&
                                            (countryProfileTaxonomyNames.quotaForInputOnly !== NOT_APPLICABLE_TEXT)
                                        ) && (
                                            <QuotaImplementationItem title="Quota on results">
                                                { countryProfileTaxonomyNames.quotaForInputOnly }
                                                { 
                                                    (countryProfileTaxonomyNames.quotaForInputOnly === 'Yes')
                                                    &&
                                                    (countryProfile.correctiveMeasureDetail.length > 0)
                                                    && (
                                                        <>
                                                            . { countryProfile.correctiveMeasureDetail }
                                                        </>
                                                    )
                                                }
                                            </QuotaImplementationItem>
                                        ) }
                                    </>
                                ) }
                            </CountryProfileSection>
                        </Box>
                    </Cell>
                </Grid>
                <Spacer/>
            </div>
            <div className={ countryProfileDashboardContent2ClassName }>
                <Box className={ countryProfileDashboardContentBoxClassName }>
                    <Grid>
                        <Cell size={ 12 } sizeDesktop={ 'stack' }>
                            <CountryProfileSection title="Quota legal source">
                                <CheckList>
                                    <CheckList.Item checked={ countryProfile.constitutionProvisionsGenderQuotaId === LEGAL_SOURCE_TRUE_ID }>
                                        Constitution
                                    </CheckList.Item>
                                    <CheckList.Item checked={ countryProfile.electoralProvisionsGenderQuotaId === LEGAL_SOURCE_TRUE_ID }>
                                        Electoral law
                                    </CheckList.Item>
                                    { countryProfile.localLevel && (
                                        <CheckList.Item checked={ countryProfile.localProvisionsGenderQuotaId === LEGAL_SOURCE_TRUE_ID }>
                                            Local government law
                                        </CheckList.Item>
                                    ) }
                                    <CheckList.Item checked={ countryProfile.parityProvisionsGenderQuotaId === LEGAL_SOURCE_TRUE_ID }>
                                        Parity law
                                    </CheckList.Item>
                                    <CheckList.Item checked={ countryProfile.partyProvisionsGenderQuotaId === LEGAL_SOURCE_TRUE_ID }>
                                        Party law
                                    </CheckList.Item>
                                    <CheckList.Item checked={ countryProfile.financingProvisionsGenderQuotaId === LEGAL_SOURCE_TRUE_ID }>
                                        Financing law
                                    </CheckList.Item>
                                    <CheckList.Item checked={ countryProfile.politicalProvisionsGenderQuotaId === LEGAL_SOURCE_TRUE_ID }>
                                        Political agreement
                                    </CheckList.Item>
                                </CheckList>
                            </CountryProfileSection>
                        </Cell>
                        <Cell size={ 12 } sizeDesktop={ 'fill' }>
                            <CountryProfileSection title="Legal provisions">
                                <div className={ countryProfileDashboardLegalProvisionsWrapperClassName }>
                                    <LegalProvisions countryProfile={ countryProfile } initExpanded/>
                                </div>
                            </CountryProfileSection>
                        </Cell>
                        <Cell size={ 12 }>
                            <CountryProfileSection>
                                <InfoDate localLevel={ countryProfile.localLevel } dates={ {
                                    date: countryProfile.date,
                                    ipuDate: countryProfile.ipuDate || countryProfile.date
                                } }/>
                            </CountryProfileSection>
                        </Cell>
                    </Grid>
                </Box>
            </div>
        </div>
    )
};
